import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, Clock, Calendar, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { BACKEND_URL, API } from "@/lib/api";


export default function EventTimeline({ eventId, isOwner }) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Mode: "single_day" = one day with times, "multi_day" = date range
  const [scheduleMode, setScheduleMode] = useState("single_day");
  const [eventDate, setEventDate] = useState(""); // For single day mode
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    time: "" // For single day mode (HH:mm format)
  });

  useEffect(() => {
    fetchTimeline();
  }, [eventId]);

  // Auto-detect mode based on existing data
  useEffect(() => {
    if (milestones.length > 0) {
      // Check if all milestones are on the same day
      const dates = milestones.map(m => m.datetime?.split('T')[0]);
      const uniqueDates = [...new Set(dates)];
      
      if (uniqueDates.length === 1 && uniqueDates[0]) {
        setScheduleMode("single_day");
        setEventDate(uniqueDates[0]);
      } else if (uniqueDates.length > 1) {
        setScheduleMode("multi_day");
      }
    }
  }, [milestones]);

  const fetchTimeline = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/timeline`);
      setMilestones(response.data.sort((a, b) => new Date(a.datetime) - new Date(b.datetime)));
    } catch (error) {
      console.error("Failed to fetch timeline:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error("Titel ist erforderlich");
      return;
    }

    // Build datetime based on mode
    let datetime;
    if (scheduleMode === "single_day") {
      if (!eventDate) {
        toast.error("Bitte wähle ein Datum für den Ablaufplan");
        return;
      }
      if (!formData.time) {
        toast.error("Bitte gib eine Uhrzeit ein");
        return;
      }
      datetime = `${eventDate}T${formData.time}:00`;
    } else {
      if (!formData.date) {
        toast.error("Bitte wähle ein Datum");
        return;
      }
      datetime = formData.date;
    }

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        datetime: datetime
      };

      if (editingId) {
        await axios.put(
          `${API}/events/${eventId}/timeline/${editingId}`,
          payload,
          { withCredentials: true }
        );
        toast.success("Eintrag aktualisiert!");
      } else {
        await axios.post(
          `${API}/events/${eventId}/timeline`,
          payload,
          { withCredentials: true }
        );
        toast.success("Eintrag hinzugefügt!");
      }
      
      resetForm();
      fetchTimeline();
    } catch (error) {
      console.error("Failed to save milestone:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleEdit = (milestone) => {
    setEditingId(milestone.id);
    const dateTimeParts = milestone.datetime?.split('T') || [];
    const date = dateTimeParts[0] || "";
    const timePart = dateTimeParts[1] || "";
    const time = timePart ? timePart.substring(0, 5) : ""; // Extract HH:mm
    
    setFormData({
      title: milestone.title,
      description: milestone.description || "",
      date: date,
      time: time
    });
    setShowAddForm(true);
  };

  const handleDelete = async (milestoneId) => {
    if (!confirm("Eintrag wirklich löschen?")) return;
    
    try {
      await axios.delete(
        `${API}/events/${eventId}/timeline/${milestoneId}`,
        { withCredentials: true }
      );
      toast.success("Eintrag gelöscht");
      fetchTimeline();
    } catch (error) {
      console.error("Failed to delete milestone:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const resetForm = () => {
    setFormData({ title: "", description: "", date: "", time: "" });
    setEditingId(null);
    setShowAddForm(false);
  };

  const formatTime = (datetime) => {
    if (!datetime) return "";
    try {
      const date = parseISO(datetime);
      return format(date, "HH:mm", { locale: de });
    } catch {
      return "";
    }
  };

  const formatDate = (datetime) => {
    if (!datetime) return "";
    try {
      const date = parseISO(datetime);
      return format(date, "dd. MMMM yyyy", { locale: de });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ListOrdered className="w-5 h-5 text-primary" />
            Ablaufplan
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {isOwner 
              ? "Plane den zeitlichen Ablauf deines Events"
              : "Zeitlicher Ablauf des Events"
            }
          </p>
        </div>
        
        {isOwner && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddForm(!showAddForm)}
            className="rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            {showAddForm ? "Abbrechen" : "Hinzufügen"}
          </Button>
        )}
      </div>

      {/* Mode Selector (only for owner) */}
      {isOwner && (
        <div className="mb-6 p-4 bg-secondary/30 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Planungsart</p>
              <p className="text-xs text-muted-foreground">
                {scheduleMode === "single_day" 
                  ? "Ein Tag mit Uhrzeiten" 
                  : "Mehrere Tage mit Datum"
                }
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs ${scheduleMode === "single_day" ? "font-medium" : "text-muted-foreground"}`}>
                Ein Tag
              </span>
              <Switch
                checked={scheduleMode === "multi_day"}
                onCheckedChange={(checked) => setScheduleMode(checked ? "multi_day" : "single_day")}
              />
              <span className={`text-xs ${scheduleMode === "multi_day" ? "font-medium" : "text-muted-foreground"}`}>
                Mehrere Tage
              </span>
            </div>
          </div>
          
          {/* Event Date for Single Day Mode */}
          {scheduleMode === "single_day" && (
            <div className="mt-4">
              <Label htmlFor="eventDate" className="text-xs">Event-Datum</Label>
              <Input
                id="eventDate"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="mt-1"
              />
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Form */}
      {isOwner && showAddForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
          <div>
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={scheduleMode === "single_day" 
                ? "z.B. Empfang, Essen, Programm, Party" 
                : "z.B. Anreise, Trauung, Feier"
              }
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Beschreibung</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Zusätzliche Details..."
              rows={2}
            />
          </div>

          {scheduleMode === "single_day" ? (
            <div>
              <Label htmlFor="time">Uhrzeit *</Label>
              <Input
                id="time"
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                required
              />
            </div>
          ) : (
            <div>
              <Label htmlFor="date">Datum *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
          )}

          <div className="flex gap-2">
            <Button type="submit" className="flex-1">
              {editingId ? "Aktualisieren" : "Hinzufügen"}
            </Button>
            {editingId && (
              <Button type="button" variant="outline" onClick={resetForm}>
                Abbrechen
              </Button>
            )}
          </div>
        </form>
      )}

      {/* Timeline/Schedule Display */}
      <div className="relative">
        {milestones.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <ListOrdered className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Noch kein Ablaufplan erstellt</p>
            {isOwner && (
              <p className="text-xs mt-1">Füge Programmpunkte hinzu!</p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Show event date header for single day mode */}
            {scheduleMode === "single_day" && eventDate && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 pb-2 border-b">
                <Calendar className="w-4 h-4" />
                <span>{format(parseISO(eventDate), "EEEE, dd. MMMM yyyy", { locale: de })}</span>
              </div>
            )}
            
            {milestones.map((milestone, index) => {
              const time = formatTime(milestone.datetime);
              const date = formatDate(milestone.datetime);
              const showDate = scheduleMode === "multi_day";

              return (
                <div key={milestone.id} className="relative pl-8">
                  {/* Timeline Line */}
                  {index !== milestones.length - 1 && (
                    <div className="absolute left-2 top-8 bottom-0 w-0.5 bg-border"></div>
                  )}
                  
                  {/* Timeline Dot */}
                  <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-primary border-4 border-background"></div>
                  
                  {/* Content */}
                  <div className="pb-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {time && (
                            <span className="text-lg font-bold text-primary">{time}</span>
                          )}
                          <h4 className="font-medium text-foreground">{milestone.title}</h4>
                        </div>
                        
                        {showDate && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <Calendar className="w-3 h-3" />
                            <span>{date}</span>
                          </div>
                        )}
                        
                        {milestone.description && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {milestone.description}
                          </p>
                        )}
                      </div>

                      {isOwner && (
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleEdit(milestone)}
                            className="h-8 w-8"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDelete(milestone.id)}
                            className="h-8 w-8 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}
